import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo
} from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { gqlType } from "@hifieng/common";
import AppError from "../errors/AppError";
import { IOrganizations } from "../types/OrganizationTypes";
import useDeepCompareEffect from "use-deep-compare-effect";
import moment from "moment-timezone";

export const GET_ORGANIZATIONS_DATA = gql`
  query {
    organizations {
      id
      name
      imageUrl
      utcOffset
      timezoneAbbr
      pipelines {
        id
        name
        color
        segments {
          id
          post
          coordinates {
            latitude
            longitude
          }
        }
        assets {
          id
          type
          post
          coordinates {
            latitude
            longitude
          }
        }
        interfaceOptions {
          hideFromMapLegend
          hideHeartbeat
          initialFocus
          zIndex
        }
        chKpMaps {
          id
          name
          ch
          kp
          precision
          step
        }
      }
    }
  }
`;

export const GET_PIPELINE_KM_POSTS = gql`
  query GetPipelineKmPosts($pipelineIds: [String!]!) {
    pipelineKmPosts(pipelineIds: $pipelineIds) {
      id
      kmPosts {
        post
        coordinates {
          latitude
          longitude
        }
      }
    }
  }
`;

export type OrganizationResponseDataType = {
  id: string;
  name: string;
  imageUrl: string;
  pipelines: Array<gqlType.Pipeline>;
  pointsOfInterest: Array<gqlType.PointOfInterest>;
};

type KmPostsDataType = {
  pipelineKmPosts: Array<gqlType.PipelineKmPostsResult>;
};

const initialContext: IOrganizations = {
  activeOrg: undefined,
  setActiveOrg: () => {
    throw new Error("Organization context has not yet been initialized.");
  },
  organizations: undefined
};

type PropsType = {
  children: React.ReactNode;
};

const applyDaylightSavings = (org?: gqlType.Organization) => {
  if (org !== undefined) {
    // Uses IANA standard for timezone names. Long name needed for Daylight Savings to be applied.
    // utcOffset setting from config is ignored if IANA long name provided as setting.
    if (moment.tz.zone(org.timezoneAbbr)) {
      // MST works here because there is only one MST. CST and PST don't work because there are multiple of those
      const momentObj = moment.tz(org.timezoneAbbr);
      const zoneAbbr = momentObj.zoneAbbr();
      const minutesInHour = 60;
      const padding = 2;

      // Orgs with MST (any IANA abbreviations) will be skipped using the condition below.
      // If org timezone already calculated this this will also be skipped since new timezone abbreviation is being used.
      // assign timezone abbreviation and calculate utc offset if the config abbr is different from actual ie. config is not abbreviated
      if (zoneAbbr !== org.timezoneAbbr) {
        const utcOffset = momentObj.utcOffset();
        org.timezoneAbbr = zoneAbbr;
        const sign = utcOffset >= 0 ? "+" : "-";
        const absOffset = Math.abs(utcOffset);
        const hour = Math.floor(absOffset / minutesInHour);
        const minutes = absOffset % minutesInHour;
        const paddedHour = String(hour).padStart(padding, "0");
        const paddedMinutes = String(minutes).padStart(padding, "0");
        org.utcOffset = `${sign}${paddedHour}:${paddedMinutes}`;
        // console.log(`New utcOffset for ${org.id}:`, org.utcOffset)
      }
    }
  }
};

const findOrgById = (
  orgId: string,
  organizations?: Array<gqlType.Organization>
) => {
  const activeOrg =
    organizations && organizations.find(org => org.id === orgId);

  return activeOrg;
};

const mergePipelinesWithKmPosts = (
  orgs: Array<gqlType.Organization>,
  kmPostsResults: Array<gqlType.PipelineKmPostsResult>
): Array<gqlType.Organization> => {
  const kmPostsById = new Map<string, Array<gqlType.Locations>>();
  kmPostsResults.forEach(result => {
    kmPostsById.set(result.id, result.kmPosts);
  });

  const totalPipelineCount = orgs.reduce(
    (count: number, org: gqlType.Organization) =>
      count + Number(org.pipelines.length),
    0
  );
  const matchedPipelineCount = orgs.reduce(
    (count: number, org: gqlType.Organization) =>
      count +
      Number(
        org.pipelines.filter((pipeline: gqlType.Pipeline) =>
          Boolean(kmPostsById.get(pipeline.id))
        ).length
      ),
    0
  );

  console.log("OrganizationProvider merge debug", {
    organizationCount: orgs.length,
    totalPipelineCount,
    kmPostsResultCount: kmPostsResults.length,
    matchedPipelineCount
  });

  return orgs.map(org => ({
    ...org,
    pipelines: org.pipelines.map(pipeline => ({
      ...pipeline,
      kmPosts: kmPostsById.get(pipeline.id) || pipeline.kmPosts
    }))
  }));
};

export const OrganizationContext = React.createContext(initialContext);
export const useOrganizationContext = () => useContext(OrganizationContext);
export const OrganizationProvider = (props: PropsType) => {
  const { error, loading, data } = useQuery<IOrganizations>(
    GET_ORGANIZATIONS_DATA
  );

  if (data) {
    if (data.organizations) {
      data.organizations.map(applyDaylightSavings);
    }
  }

  // Collect all pipeline ids once orgs are loaded so we can batch-fetch km posts.
  const allPipelineIds = useMemo(() => {
    if (!data || !data.organizations) return [];
    const pipelineIds = data.organizations.reduce(
      (acc: Array<string>, org: gqlType.Organization) =>
        acc.concat(org.pipelines.map(p => p.id)),
      []
    );

    console.log("OrganizationProvider pipeline id debug", {
      organizationCount: data.organizations.length,
      pipelineIdCount: pipelineIds.length,
      samplePipelineId: pipelineIds.length > 0 ? pipelineIds[0] : "none"
    });

    return pipelineIds;
  }, [data]);

  // cache-first ensures km posts are fetched once and served from cache on
  // subsequent renders — avoids redundant network requests as the user
  // navigates between orgs.
  const { data: kmPostsData } = useQuery<KmPostsDataType>(
    GET_PIPELINE_KM_POSTS,
    {
      variables: { pipelineIds: allPipelineIds },
      skip: allPipelineIds.length === 0,
      fetchPolicy: "cache-first",
      onCompleted: completedData => {
        console.log("OrganizationProvider kmPosts query completed", {
          requestedPipelineIds: allPipelineIds.length,
          returnedRows: completedData.pipelineKmPosts.length
        });
      }
    }
  );

  const [activeOrgId, setActiveOrgId] = useState<string>();
  const [organizations, setOrganizations] = useState<
    Array<gqlType.Organization>
  >();

  if (error) {
    throw new AppError(
      "Organization information could not be retrieved",
      error.message
    );
  }

  if (!data && !loading) {
    throw new AppError(
      "Organization data could not be fetched",
      "Refresh the page to try again. If this problem persists, contact Hifi support."
    );
  }

  // The function that allows other parts of the app to change the organization
  // useCallback is used so that the components that implement this context don't render every time
  // this provider re-renders.
  const setActiveOrg = useCallback(
    (orgId: string) => {
      if (!organizations) {
        throw new Error(
          "You can't switch organizations while the application is loading"
        );
      }
      if (!findOrgById(orgId, organizations)) {
        throw new AppError(
          `Cannot switch to Organization "${orgId}" because it does not exist`,
          "Please refresh your browser and try again."
        );
      }

      setActiveOrgId(orgId);
      localStorage.setItem("hifi:activeOrg", orgId);
    },
    [organizations]
  );

  // useQuery has an internal caching mechanism that causes `data` to update more often than
  // necessary. The LiveStatus polling queries cause this provider to update as well. Doing a deep
  // comparison is a good way to check if the organizations have actually changed.
  useDeepCompareEffect(() => {
    if (data) {
      const orgs = data.organizations;

      console.log("OrganizationProvider organizations query debug", {
        hasOrganizations: Boolean(orgs),
        organizationCount: orgs ? orgs.length : 0,
        pipelineIdsRequested: allPipelineIds.length,
        hasKmPostsData: Boolean(kmPostsData && kmPostsData.pipelineKmPosts),
        kmPostsRowCount:
          kmPostsData && kmPostsData.pipelineKmPosts
            ? kmPostsData.pipelineKmPosts.length
            : 0
      });

      if (orgs && kmPostsData && kmPostsData.pipelineKmPosts) {
        setOrganizations(
          mergePipelinesWithKmPosts(orgs, kmPostsData.pipelineKmPosts)
        );
      } else {
        setOrganizations(orgs);
      }

      console.log(
        `OrganizationProvider: ${String(
          orgs ? orgs.length : 0
        )} organizations loaded`
      );
      console.log(
        `OrganizationProvider: ${String(
          kmPostsData && kmPostsData.pipelineKmPosts
            ? kmPostsData.pipelineKmPosts.length
            : "No data"
        )}`
      );
    }
  }, [data || {}, kmPostsData || {}, allPipelineIds]);

  // This should only run on application startup when there is no active org ID yet.
  // If the user does not have an active org defined, grab it from localhost, or fall back to
  // picking the first one.
  useEffect(() => {
    if (organizations && !activeOrgId) {
      // User must belong to at least one org
      if (organizations.length === 0) {
        throw new AppError(
          "Your account is not associated with any organizations",
          "Contact Hifi support to resolve this issue."
        );
      }

      const previouslyActiveOrgId = localStorage.getItem("hifi:activeOrg");
      if (
        previouslyActiveOrgId &&
        findOrgById(previouslyActiveOrgId, organizations)
      ) {
        // If an org was previously active, restore it
        setActiveOrgId(previouslyActiveOrgId);
      } else {
        // Otherwise, just pick the first org
        setActiveOrgId(organizations[0].id);
      }
    }
  }, [activeOrgId, organizations]);

  // Memoize the provider value, otherwise on every re-render the value will change triggering
  // re-renders throughout the app. If memo isn't used, the post markers on the events map will
  // flicker due to organization "changing" on every re-render.
  const providerValue = useMemo(() => {
    if (!organizations || !activeOrgId) return initialContext;

    return {
      setActiveOrg,
      organizations,
      activeOrg: findOrgById(activeOrgId, organizations)
    };
  }, [setActiveOrg, organizations, activeOrgId]);

  return (
    <OrganizationContext.Provider value={providerValue}>
      {props.children}
    </OrganizationContext.Provider>
  );
};
